import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { ErrorState } from '../components/guidance/error-state';
import { PageHeader } from '../components/layout/app-shell';
import { AdvancedBuilder } from '../components/report-document/advanced-builder';
import { NewReportFlow } from '../components/reports/new-report/new-report-flow';
import { Button } from '../components/ui/button';
import { useReportTemplates } from '../lib/reports';
import { useSchema } from '../lib/schema';

// A new report (Report v2, the creation flow): describe it in words, check the plan the AI drafts from the
// description, then create it — the report is created, drafted section by section, given its suggested figures and
// opened as a document. `?advanced=1` (Advanced only) is the full builder for a new, unsaved report — the question,
// results, exports, charts, network centrality and schedules, the same builder a saved report's Advanced drawer
// mounts; `?template=…` preselects a template on either path.

export const Route = createFileRoute('/reports/new')({
  validateSearch: (s: Record<string, unknown>): { template?: string; advanced?: boolean } => ({
    ...(typeof s.template === 'string' && s.template ? { template: s.template } : {}),
    ...(s.advanced === true || s.advanced === 1 || s.advanced === '1' ? { advanced: true } : {}),
  }),
  component: NewReportPage,
});

function NewReportPage() {
  const { template: templateId, advanced } = Route.useSearch();
  const { data: snap, error: schemaError, refetch } = useSchema();
  const templates = useReportTemplates();
  const back = (
    <Button size="sm" variant="ghost" asChild>
      <Link to="/reports">
        <ArrowLeft aria-hidden="true" /> All reports
      </Link>
    </Button>
  );
  if (schemaError || templates.isError)
    return (
      <>
        <PageHeader title="New report" actions={back} />
        <ErrorState
          error={schemaError ?? templates.error}
          title="A new report can’t be started"
          onRetry={() => (schemaError ? refetch() : templates.refetch())}
          learnMore="build-a-report"
        />
      </>
    );
  if (!snap || templates.isPending)
    return (
      <div aria-busy="true" className="space-y-3">
        <span className="sr-only">Loading…</span>
        <div className="h-8 w-64 animate-pulse rounded-md bg-muted" />
        <div className="h-40 animate-pulse rounded-md bg-muted" />
      </div>
    );
  const list = templates.data.templates;
  if (advanced) {
    const template = templateId ? list.find(t => t.id === templateId) : undefined;
    return (
      <AdvancedBuilder
        key={templateId ?? ''}
        snap={snap}
        report={null}
        templateId={template?.id ?? null}
        templateName={template?.name ?? null}
        templateSections={template?.sections ?? []}
        back={back}
      />
    );
  }
  return <NewReportFlow snap={snap} templateId={templateId ?? null} templates={list} back={back} />;
}
